import moment from "moment-timezone";

import { Operator } from "../operators";
import { ColumnKind, Data } from "../shared";

const { Date, Enum, Money, Text, Time } = ColumnKind;
const {
  IsEmpty,
  IsNotEmpty,
  IsTrue,
  IsFalse,
  Equal,
  NotEqual,
  Contains,
  DoesNotContain,
  LessThan,
  LessThanOrEqual,
  GreaterThan,
  GreaterThanOrEqual,
  After,
  Before,
  Between,
  CurrentDay,
  PreviousDay,
  LastSevenDays,
  LastThirtyDays,
  LastThreeMonths,
  LastSixMonths,
  LastTwelveMonths,
  NextSevenDays,
  NextThirtyDays,
  NextThreeMonths,
} = Operator;

// Handles date filters for which the end value is known and not arbitrary
const handlePresetComparison = (t1, operator) => {
  const startOfDayToday = moment().startOf("day");
  const endOfDayToday = moment().endOf("day");

  // moment functions mutate the original value,
  // so create 2 dummy variables for comparison later
  const sod = moment().startOf("day");
  const eod = moment().endOf("day");

  if (operator === CurrentDay) {
    return t1.isSame(moment(), "day");
  }
  if (operator === PreviousDay) {
    return t1.isSame(sod.subtract(1, "days"), "days");
  }
  if (operator === LastSevenDays) {
    return t1.isBetween(sod.subtract(7, "days"), endOfDayToday, "seconds");
  }
  if (operator === LastThirtyDays) {
    return t1.isBetween(sod.subtract(30, "days"), endOfDayToday, "seconds");
  }
  if (operator === LastThreeMonths) {
    return t1.isBetween(sod.subtract(3, "months"), endOfDayToday, "seconds");
  }
  if (operator === LastSixMonths) {
    return t1.isBetween(sod.subtract(6, "months"), endOfDayToday, "seconds");
  }
  if (operator === LastTwelveMonths) {
    return t1.isBetween(sod.subtract(12, "months"), endOfDayToday, "seconds");
  }
  if (operator === NextSevenDays) {
    return t1.isBetween(startOfDayToday, eod.add(7, "days"), "seconds");
  }
  if (operator === NextThirtyDays) {
    return t1.isBetween(startOfDayToday, eod.add(30, "days"), "seconds");
  }
  if (operator === NextThreeMonths) {
    return t1.isBetween(startOfDayToday, eod.add(3, "months"), "seconds");
  }
};

const comparisonRequiresSingleValue = operator =>
  [Operator.Equal, Operator.NotEqual, Operator.After, Operator.Before].includes(operator);

const isPresetComparison = operator =>
  [
    CurrentDay,
    LastSevenDays,
    LastThirtyDays,
    LastThreeMonths,
    LastSixMonths,
    LastTwelveMonths,
    NextSevenDays,
    NextThirtyDays,
    NextThreeMonths,
  ].includes(operator);

export const dateHandler = (timestamp, jsonKey, operator, unitScale: any = "day"): Function => {
  const handler = (data: Data) => {
    return data.filter(d => {
      const currentItem = d[jsonKey];
      const t1 = moment(currentItem as string);

      // If t1 is invalid, current datapoint is (likely) null, so omit from the filtered results
      if (!t1.isValid) {
        return false;
      }

      // If we're not comparing against arbitrary values, we can filter without computing t2/t3
      if (isPresetComparison(operator)) {
        return handlePresetComparison(t1, operator);
      }

      // If it's a singular value, use it. Else, it's a date range array for "Between", so use the first value
      const t2 = comparisonRequiresSingleValue(operator) ? moment(timestamp) : moment(timestamp[0]);

      if (operator === Equal) {
        return t1.isSame(t2, unitScale);
      }
      if (operator === NotEqual) {
        return !t1.isSame(t2, unitScale);
      }
      if (operator === Before) {
        return t1.isBefore(t2, unitScale);
      }
      if (operator === After) {
        return t1.isAfter(t2, unitScale);
      }
      if (operator === Between) {
        const t3 = moment(timestamp[1]);
        return t1.isBetween(t2, t3, unitScale);
      }

      throw new Error(`${operator} is not supported for date/time filters`);
    });
  };

  return handler;
};

export const textHandler = (str: string, jsonKey, operator): Function => {
  const handler = (data: Data) => {
    return data.filter(d => {
      // Clean strings before comparison
      const s1 = `${d[jsonKey]}`.trim().toLowerCase();
      const s2 = str.trim().toLowerCase();

      if (operator === Equal) {
        return s1 === s2;
      }
      if (operator === NotEqual) {
        return s1 !== s2;
      }
      if (operator === Contains) {
        return s1.includes(s2);
      }
      if (operator === DoesNotContain) {
        return !s1.includes(s2);
      }

      throw new Error(`${operator} is not supported for text filters`);
    });
  };
  return handler;
};

export const numberHandler = (num: number, jsonKey, operator, isCurrency = false): Function => {
  const handler = (data: Data) => {
    return data.filter(d => {
      const currentItem = d[jsonKey];
      const field = isCurrency ? parseFloat(currentItem as string) : currentItem;

      if (operator === Equal) {
        return field === num;
      }
      if (operator === NotEqual) {
        return field !== num;
      }
      if (operator === LessThan) {
        return field < num;
      }
      if (operator === LessThanOrEqual) {
        return field <= num;
      }
      if (operator === GreaterThan) {
        return field > num;
      }
      if (operator === GreaterThanOrEqual) {
        return field >= num;
      }

      throw new Error(`${operator} is not supported for number filters`);
    });
  };
  return handler;
};

export const handlerMap = (
  kind: ColumnKind,
  operator: Operator,
  jsonKey: string,
  comparisonValue?: any,
): Function => {
  const universalHandlers = {
    [IsNotEmpty]: (data: Data) =>
      data.filter(d => d[jsonKey] !== null && d[jsonKey] !== undefined && d[jsonKey] !== ""),
    [IsEmpty]: (data: Data) =>
      data.filter(d => d[jsonKey] === null || d[jsonKey] === undefined || d[jsonKey] === ""),
  };

  const dateTimeHandlers = {
    ...universalHandlers,
    [Equal]: dateHandler(comparisonValue, jsonKey, operator, "day"),
    [NotEqual]: dateHandler(comparisonValue, jsonKey, operator, "day"),
    [Before]: dateHandler(comparisonValue, jsonKey, operator, "day"),
    [After]: dateHandler(comparisonValue, jsonKey, operator, "day"),
    [Between]: dateHandler(comparisonValue, jsonKey, operator, "day"),
    [CurrentDay]: dateHandler(comparisonValue, jsonKey, operator),
    [PreviousDay]: dateHandler(comparisonValue, jsonKey, operator),
    [LastSevenDays]: dateHandler(comparisonValue, jsonKey, operator),
    [LastThirtyDays]: dateHandler(comparisonValue, jsonKey, operator),
    [LastThreeMonths]: dateHandler(comparisonValue, jsonKey, operator),
    [LastSixMonths]: dateHandler(comparisonValue, jsonKey, operator),
    [LastTwelveMonths]: dateHandler(comparisonValue, jsonKey, operator),
    [NextSevenDays]: dateHandler(comparisonValue, jsonKey, operator),
    [NextThirtyDays]: dateHandler(comparisonValue, jsonKey, operator),
    [NextThreeMonths]: dateHandler(comparisonValue, jsonKey, operator),
  };

  const fnsMap = {
    [ColumnKind.Boolean]: {
      ...universalHandlers,
      [IsTrue]: (data: Data) => data.filter(d => d[jsonKey] === true),
      [IsFalse]: (data: Data) => data.filter(d => d[jsonKey] === false),
    },
    [Date]: dateTimeHandlers,
    [Time]: dateTimeHandlers,
    [Enum]: {
      ...universalHandlers,
      [Equal]: (data: Data) => data.filter(d => comparisonValue.includes(d[jsonKey] as string)),
      [NotEqual]: (data: Data) => data.filter(d => !comparisonValue.includes(d[jsonKey] as string)),
    },
    [ColumnKind.Number]: {
      ...universalHandlers,
      [Equal]: numberHandler(comparisonValue, jsonKey, operator),
      [NotEqual]: numberHandler(comparisonValue, jsonKey, operator),
      [LessThan]: numberHandler(comparisonValue, jsonKey, operator),
      [LessThanOrEqual]: numberHandler(comparisonValue, jsonKey, operator),
      [GreaterThan]: numberHandler(comparisonValue, jsonKey, operator),
      [GreaterThanOrEqual]: numberHandler(comparisonValue, jsonKey, operator),
    },
    [Money]: {
      ...universalHandlers,
      [Equal]: numberHandler(comparisonValue, jsonKey, operator, true),
      [NotEqual]: numberHandler(comparisonValue, jsonKey, operator, true),
      [LessThan]: numberHandler(comparisonValue, jsonKey, operator, true),
      [LessThanOrEqual]: numberHandler(comparisonValue, jsonKey, operator, true),
      [GreaterThan]: numberHandler(comparisonValue, jsonKey, operator, true),
      [GreaterThanOrEqual]: numberHandler(comparisonValue, jsonKey, operator, true),
    },
    [Text]: {
      ...universalHandlers,
      [Equal]: textHandler(comparisonValue, jsonKey, operator),
      [NotEqual]: textHandler(comparisonValue, jsonKey, operator),
      [Contains]: textHandler(comparisonValue, jsonKey, operator),
      [DoesNotContain]: textHandler(comparisonValue, jsonKey, operator),
    },
  };

  return fnsMap[kind][operator] as Function;
};
